import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button, Card } from '@/components/ui';
import { parseLocalDate, parseLocalDateTime } from '@/lib/date';
import { KPICard } from './components/KPICard';
import {
  type SpendDailyRow,
  type SpendHourlyRow,
  useAdminLlmSpend,
} from './hooks/useAdminLlmSpend';

function formatUsd(n: number): string {
  // 4 decimals so sub-cent costs are visible (matches AdminLlmRuns).
  return `$${n.toFixed(4)}`;
}

function formatDay(iso: string): string {
  const d = parseLocalDate(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatHour(iso: string): string {
  const d = parseLocalDateTime(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatMs(ms: number | null): string {
  if (ms === null) return '—';
  return `${ms.toFixed(0)} ms`;
}

function formatTokens(n: number | null): string {
  if (n === null) return '—';
  return n.toLocaleString('en-US');
}

interface AdminLlmSpendProps {
  password: string;
}

export function AdminLlmSpend({ password }: AdminLlmSpendProps) {
  const {
    status,
    totals,
    daily,
    alert,
    hourly,
    error,
    lastFetchedAt,
    refetch,
  } = useAdminLlmSpend(password);

  if (status === 'loading' && !totals) {
    return <div className="text-muted-foreground">Loading LLM spend…</div>;
  }
  if (status === 'error' && !totals) {
    return (
      <Card className="p-4 border-danger">
        <div className="text-sm text-danger">
          Failed to load LLM spend: {error}
        </div>
        <Button
          variant="secondary"
          onClick={refetch}
          className="mt-3"
        >
          Retry
        </Button>
      </Card>
    );
  }

  // Sort daily desc by day for display (server already returns desc, but be
  // defensive — a future change to the edge fn shouldn't break the UI).
  const sortedDaily: SpendDailyRow[] = [...daily].sort((a, b) =>
    a.day < b.day ? 1 : a.day > b.day ? -1 : 0,
  );

  const sortedHourly: SpendHourlyRow[] = [...hourly].sort((a, b) =>
    a.hour < b.hour ? 1 : a.hour > b.hour ? -1 : 0,
  );

  const isEmpty = (totals?.today.calls ?? 0) === 0 && sortedDaily.length === 0;

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          Last refreshed:{' '}
          {lastFetchedAt ? lastFetchedAt.toLocaleTimeString() : '—'}
        </div>
        <Button
          variant="secondary"
          onClick={refetch}
          disabled={status === 'loading'}
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${status === 'loading' ? 'animate-spin' : ''}`}
          />
          Refresh
        </Button>
      </div>

      {/* Spend alert banner */}
      {alert && alert.today_exceeds_threshold ? (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-lg border border-warning-border bg-warning-light p-4 text-foreground"
        >
          <AlertTriangle
            size={20}
            className="shrink-0 mt-0.5 text-warning"
            aria-hidden="true"
          />
          <div className="flex flex-col gap-1">
            <div className="font-semibold text-sm">LLM spend alert</div>
            <div className="text-sm">
              Today's spend ({formatUsd(totals?.today.cost_usd ?? 0)}) has
              crossed the alert threshold ({formatUsd(alert.threshold_usd)}).
              Investigate.
            </div>
          </div>
        </div>
      ) : null}

      {/* KPI cards: Today / 7d / 30d */}
      {totals ? (
        <section className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">Spend totals</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <KPICard
              label="Today"
              value={formatUsd(totals.today.cost_usd)}
              hint={`${totals.today.calls} calls · ${totals.today.error_count} errors`}
            />
            <KPICard
              label="Last 7 days"
              value={formatUsd(totals.last_7d.cost_usd)}
              hint={`${totals.last_7d.calls} calls · ${totals.last_7d.error_count} errors`}
            />
            <KPICard
              label="Last 30 days"
              value={formatUsd(totals.last_30d.cost_usd)}
              hint={`${totals.last_30d.calls} calls · ${totals.last_30d.error_count} errors`}
            />
          </div>
        </section>
      ) : null}

      {/* Empty state */}
      {isEmpty ? (
        <Card className="p-8 text-center">
          <div className="text-muted-foreground text-sm">
            No LLM data yet — run a focus-group baseline or process some emails.
          </div>
        </Card>
      ) : null}

      {/* Daily breakdown */}
      {sortedDaily.length > 0 ? (
        <section className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">
            Daily breakdown (last 30 days)
          </h2>
          <Card
            variant="default"
            padding="none"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="p-3 font-medium">Day</th>
                    <th className="p-3 font-medium">Provider</th>
                    <th className="p-3 font-medium">Model</th>
                    <th className="p-3 font-medium text-right">Calls</th>
                    <th className="p-3 font-medium text-right">Success</th>
                    <th className="p-3 font-medium text-right">Errors</th>
                    <th className="p-3 font-medium text-right">Fallback</th>
                    <th className="p-3 font-medium text-right">
                      Prompt tokens
                    </th>
                    <th className="p-3 font-medium text-right">
                      Completion tokens
                    </th>
                    <th className="p-3 font-medium text-right">p95</th>
                    <th className="p-3 font-medium text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDaily.map((r) => (
                    <tr
                      key={`${r.day}|${r.provider}|${r.model}`}
                      className="border-b border-border last:border-b-0"
                    >
                      <td className="p-3 text-foreground whitespace-nowrap">
                        {formatDay(r.day)}
                      </td>
                      <td className="p-3 text-muted-foreground">
                        {r.provider}
                      </td>
                      <td className="p-3 text-muted-foreground font-mono text-xs">
                        {r.model}
                      </td>
                      <td className="p-3 text-right text-foreground">
                        {r.call_count}
                      </td>
                      <td className="p-3 text-right text-muted-foreground">
                        {r.success_count}
                      </td>
                      <td className="p-3 text-right text-muted-foreground">
                        {r.error_count}
                      </td>
                      <td className="p-3 text-right text-muted-foreground">
                        {r.fallback_count}
                      </td>
                      <td className="p-3 text-right text-muted-foreground">
                        {formatTokens(r.total_prompt_tokens)}
                      </td>
                      <td className="p-3 text-right text-muted-foreground">
                        {formatTokens(r.total_completion_tokens)}
                      </td>
                      <td className="p-3 text-right text-muted-foreground">
                        {formatMs(r.p95_latency_ms)}
                      </td>
                      <td className="p-3 text-right font-medium">
                        {formatUsd(r.cost_usd)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </section>
      ) : null}

      {/* Hourly rate observability */}
      {sortedHourly.length > 0 ? (
        <section className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">
            Hourly call rate (last 48 hours)
          </h2>
          <Card
            variant="default"
            padding="none"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="p-3 font-medium">Hour</th>
                    <th className="p-3 font-medium">Provider</th>
                    <th className="p-3 font-medium text-right">Calls</th>
                    <th className="p-3 font-medium text-right">Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedHourly.map((r) => (
                    <tr
                      key={`${r.hour}|${r.provider}`}
                      className="border-b border-border last:border-b-0"
                    >
                      <td className="p-3 text-foreground whitespace-nowrap">
                        {formatHour(r.hour)}
                      </td>
                      <td className="p-3 text-muted-foreground">
                        {r.provider}
                      </td>
                      <td className="p-3 text-right text-foreground">
                        {r.call_count}
                      </td>
                      <td className="p-3 text-right text-muted-foreground">
                        {r.error_count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </section>
      ) : null}
    </div>
  );
}
